/**
 * consent.js — cookie consent management.
 * Direct port of lib/cookies.ts + the consent-gated loaders from
 * components/analytics-scripts.tsx. Same cookie name/shape and the same
 * `cookieConsentChanged` window event, so behavior matches the headless app
 * (including the opt-out default: no decision yet => consent assumed).
 */
(function () {
  "use strict";

  const CONSENT_COOKIE_NAME = "cookie_consent";
  const CONSENT_COOKIE_EXPIRY_DAYS = 365;

  /* ---------------------------- cookies ----------------------------- */
  function getCookie(name) {
    const match = document.cookie
      .split("; ")
      .find((row) => row.startsWith(name + "="));
    return match ? decodeURIComponent(match.slice(name.length + 1)) : undefined;
  }

  function setCookie(name, value, days, domain) {
    let str =
      name +
      "=" +
      encodeURIComponent(value) +
      "; path=/; samesite=lax; expires=" +
      new Date(Date.now() + days * 864e5).toUTCString();
    if (location.protocol === "https:") str += "; secure";
    if (domain) str += "; domain=" + domain;
    document.cookie = str;
  }

  function removeCookie(name, domain) {
    let str = name + "=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
    if (domain) str += "; domain=" + domain;
    document.cookie = str;
  }

  function removeCookieEverywhere(name) {
    removeCookie(name);
    removeCookie(name, location.hostname);
    removeCookie(name, "." + location.hostname.replace(/^www\./, ""));
  }

  /* ------------------------- consent state -------------------------- */
  function getCookieConsent() {
    try {
      const raw = getCookie(CONSENT_COOKIE_NAME);
      if (!raw) return null;
      const consent = JSON.parse(raw);
      consent.necessary = true;
      return consent;
    } catch (e) {
      return null;
    }
  }

  function hasConsentDecision() {
    return getCookieConsent() !== null;
  }

  function hasConsent(category) {
    if (category === "necessary") return true;
    const consent = getCookieConsent();
    if (!consent) return true; // Opt-out model: default to true (matches lib/cookies.ts)
    return consent[category] === true;
  }

  function removeAnalyticsCookies() {
    document.cookie
      .split(";")
      .map((c) => c.split("=")[0].trim())
      .filter((n) => n.indexOf("_ga") === 0)
      .forEach(removeCookieEverywhere);
    removeCookieEverywhere("_gid");
  }

  function removeMarketingCookies() {
    ["_fbp", "_fbc", "_gcl_au", "_gcl_aw"].forEach(removeCookieEverywhere);
  }

  /* ------------------ Shopify Customer Privacy API ------------------ */
  /* Push our decision into Shopify's consent so it gates the native
     tracking (Meta Pixel / CAPI + analytics apps) that runs through
     Shopify Customer Events. Without this the native pixel ignores the
     banner. The store's built-in cookie banner must be disabled in
     Settings → Customer privacy so this is the only banner shown.

     Our two UI toggles map onto Shopify's four categories:
       analytics toggle  -> analytics + preferences (measurement/functional)
       marketing toggle  -> marketing + sale_of_data (advertising/ad data) */
  function toShopifyConsent(consent) {
    return {
      analytics: !!consent.analytics,
      preferences: !!consent.analytics,
      marketing: !!consent.marketing,
      sale_of_data: !!consent.marketing,
    };
  }

  function withCustomerPrivacy(cb) {
    const S = window.Shopify;
    if (!S) return; // not on a Shopify storefront (e.g. local preview)
    if (S.customerPrivacy) {
      cb(S.customerPrivacy);
    } else if (typeof S.loadFeatures === "function") {
      S.loadFeatures(
        [{ name: "consent-tracking-api", version: "0.1" }],
        (err) => {
          if (!err && S.customerPrivacy) cb(S.customerPrivacy);
        },
      );
    }
  }

  function shopifyConsentMatches(cp, target) {
    if (typeof cp.currentVisitorConsent !== "function") return false;
    const cur = cp.currentVisitorConsent() || {};
    return ["analytics", "marketing", "preferences", "sale_of_data"].every(
      (k) => (cur[k] === "yes") === !!target[k],
    );
  }

  function syncShopifyConsent(consent) {
    const target = toShopifyConsent(consent);
    withCustomerPrivacy((cp) => {
      if (shopifyConsentMatches(cp, target)) return; // already in sync
      try {
        cp.setTrackingConsent(target, function () {});
      } catch (e) {
        /* API shape changed or unavailable — fail open, no tracking change */
      }
    });
  }

  function setCookieConsent(partial) {
    const fullConsent = {
      necessary: true,
      analytics: !!partial.analytics,
      marketing: !!partial.marketing,
      timestamp: new Date().toISOString(),
    };
    setCookie(
      CONSENT_COOKIE_NAME,
      JSON.stringify(fullConsent),
      CONSENT_COOKIE_EXPIRY_DAYS,
    );
    window.dispatchEvent(
      new CustomEvent("cookieConsentChanged", { detail: fullConsent }),
    );
    if (!partial.analytics) removeAnalyticsCookies();
    if (!partial.marketing) removeMarketingCookies();
    syncShopifyConsent(fullConsent);
    syncUI();
  }

  const acceptAllCookies = () =>
    setCookieConsent({ analytics: true, marketing: true });
  const acceptNecessaryCookies = () =>
    setCookieConsent({ analytics: false, marketing: false });

  /* ------------------------------ UI -------------------------------- */
  /* Banner: #cookie-banner, shown only before a decision exists.
     Buttons: [data-consent-accept-all], [data-consent-necessary],
              [data-modal-open="cookie-settings-dialog"] (opens settings).
     Dialog:  #cookie-settings-dialog with
              [data-consent-checkbox="analytics"|"marketing"] and
              [data-consent-save]. */
  function syncUI() {
    // In the theme editor the consent cookie can't persist, so the banner
    // would otherwise reappear on every render and obstruct editing. Keep it
    // hidden in design mode; live behavior is unchanged.
    const inEditor = !!(window.Shopify && window.Shopify.designMode);
    const banner = document.getElementById("cookie-banner");
    if (banner) banner.toggleAttribute("hidden", inEditor || hasConsentDecision());

    const consent = getCookieConsent();
    document.querySelectorAll("[data-consent-checkbox]").forEach((box) => {
      const category = box.getAttribute("data-consent-checkbox");
      box.checked = consent ? consent[category] === true : false;
    });
  }

  document.addEventListener("click", (e) => {
    if (e.target.closest("[data-consent-accept-all]")) {
      acceptAllCookies();
      window.themeUtils && window.themeUtils.closeModal("cookie-settings-dialog");
    } else if (e.target.closest("[data-consent-necessary]")) {
      acceptNecessaryCookies();
      window.themeUtils && window.themeUtils.closeModal("cookie-settings-dialog");
    } else if (e.target.closest("[data-consent-save]")) {
      const get = (cat) => {
        const box = document.querySelector(
          '[data-consent-checkbox="' + cat + '"]',
        );
        return box ? box.checked : false;
      };
      setCookieConsent({ analytics: get("analytics"), marketing: get("marketing") });
      window.themeUtils && window.themeUtils.closeModal("cookie-settings-dialog");
    }
  });

  /* ------------------------------ boot ------------------------------ */
  function boot() {
    syncUI();
    // Returning visitor with a stored decision: re-assert it to Shopify so
    // its consent state stays in sync across sessions/devices.
    const stored = getCookieConsent();
    if (stored) syncShopifyConsent(stored);
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }

  window.cookieConsent = {
    get: getCookieConsent,
    set: setCookieConsent,
    hasConsent,
    hasConsentDecision,
    hasAnalyticsConsent: () => hasConsent("analytics"),
    hasMarketingConsent: () => hasConsent("marketing"),
    acceptAll: acceptAllCookies,
    acceptNecessary: acceptNecessaryCookies,
  };
})();
